import { clinicNow, toClinicDate } from './clinicTime';
import { getPhoneLength, validatePhone } from './phone';

/**
 * What stops Register New Patient / Edit Patient from saving, in one place.
 *
 * The form used to decide this three different ways. The save checked every field. The Register
 * button was disabled on `formErrors`, which only held the fields the receptionist had already
 * typed in or left. The footer counted those same `formErrors`. So a form that had just been opened
 * had Register switched ON, and the footer's "N fields still need something" went UP as the
 * receptionist worked down the form: each field they visited and left empty was added to it. The
 * count measured where they had been, not what was missing.
 *
 * Now there is one list, `patientFormBlockers`: every field that would stop the save right now,
 * whether it has been touched or not, in the order the fields appear on screen. The Register button,
 * the footer message and the save itself all read it, so they cannot disagree. The inline message
 * under each field still waits until that field has been left (or a save attempted), so a form that
 * has just been opened is not painted red; only the footer and the button look at the whole list.
 *
 * Things that are not a rule about a field keep their own handling: the look-alike check, a pending
 * upload, an error the server sends back.
 */

// ---- the checks the rules are built from ---------------------------------------------------------

/** A Pakistani CNIC (or B-Form, same shape) in the form's 42401-1234567-8 format. */
export const isValidCnic = (value) => /^[0-9]{5}-[0-9]{7}-[0-9]{1}$/.test(String(value || ''));

/** At least two characters, letters and spaces only. */
export const isValidName = (value) => {
  const name = String(value || '');
  return name.trim().length >= 2 && /^[a-zA-Z\s]+$/.test(name);
};

/** The longest first or last name the record holds (Patient and PatientCreateRequest, @Size(max = 50)). */
export const NAME_MAX_LENGTH = 50;

/** Whole years old today on the clinic's calendar; 0 when there is no usable date. */
export const calculateAgeYears = (dateOfBirth) => {
  if (!dateOfBirth) return 0;
  const today = clinicNow();
  const birthDate = toClinicDate(dateOfBirth);
  if (!birthDate) return 0;
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

/** A birth year between 0 and 120 years ago. */
const isPlausibleBirthYear = (dateOfBirth) => {
  if (!dateOfBirth) return false;
  const today = clinicNow();
  const birthDate = toClinicDate(dateOfBirth);
  if (!birthDate) return false;
  const age = today.getFullYear() - birthDate.getFullYear();
  return age >= 0 && age <= 120;
};

/**
 * A birth date that says "child". Guarded on the date being present at all: with no date,
 * calculateAgeYears reads 0, which would make every half-filled form look like a baby.
 */
export const isChildForm = (data) => !!data.dateOfBirth && calculateAgeYears(data.dateOfBirth) < 18;

const text = (value) => String(value == null ? '' : value);
const blank = (value) => !text(value).trim();

/**
 * Whether this save has to record a status date and reason. It does when an edit moves the status
 * off the one the file opened with, in either direction. It also does when the file keeps a status
 * other than Active that has no date or reason on record: the server wants both with every save of
 * such a status, so without them Save Changes would look ready and then be refused.
 *
 * @param {object} data  the form's values
 * @param {{ originalStatus?: string|null, onFile?: object|null }} [options]  the status the edited
 *        file opened with and the file as it was loaded; both null for a new registration
 */
export const statusNeedsRecording = (data, { originalStatus = null, onFile = null } = {}) => {
  if (originalStatus == null) return false;
  if (data.status !== originalStatus) return true;
  if ((data.status || 'ACTIVE') === 'ACTIVE' || !onFile) return false;
  return !onFile.statusChangedAt || blank(onFile.statusReason);
};

/**
 * An older child's file may be saved without a parent's CNIC (`_parentCnicOptional`), but only
 * while it still has another number the server takes in its place: a guardian's CNIC on file, or a
 * B-Form. Without either, the server refuses the save ("This child has no CNIC on file").
 */
const parentCnicExcused = (data, onFile) =>
  !!data._parentCnicOptional && (!blank(onFile && onFile.guardianCnic) || !blank(data.bFormNumber));

const missing = (message) => ({ kind: 'missing', message });
const invalid = (message) => ({ kind: 'invalid', message });

const phoneDigitsMessage = (value) => {
  const m = text(value).match(/^\+(\d{1,4})-/);
  const cc = m ? m[1] : '92';
  const { min, max } = getPhoneLength(cc);
  return min === max ? `Enter ${min} digits for +${cc} numbers` : `Enter ${min} to ${max} digits for +${cc} numbers`;
};

// ---- the fields ----------------------------------------------------------------------------------

/**
 * Every field a rule can hold the save on, in the order the form shows them: name, phone, date of
 * birth (or age), gender, the child's B-Form or the adult's CNIC, the child's parents, the city, and
 * on an edit the status change. "Take me there" goes to the first one, so this order is the order
 * the receptionist reads the form in.
 */
export const PATIENT_FORM_FIELDS = [
  'firstName',
  'lastName',
  'phoneNumber',
  'dateOfBirth',
  'gender',
  'bFormNumber',
  'cnic',
  'fatherName',
  'motherName',
  'parentCnicOwner',
  'fatherCnic',
  'motherCnic',
  'city',
  'statusChangedAt',
  'statusReason',
];

const LABELS = {
  firstName: 'First Name',
  lastName: 'Last Name',
  phoneNumber: 'Phone Number',
  gender: 'Gender',
  bFormNumber: 'B-Form Number',
  cnic: 'CNIC',
  fatherName: 'Father Name',
  motherName: 'Mother Name',
  parentCnicOwner: "Which parent's CNIC",
  fatherCnic: "Father's CNIC",
  motherCnic: "Mother's CNIC",
  city: 'City',
  statusChangedAt: 'Status Change Date',
  statusReason: 'Reason',
};

/** The label the form shows above a field. The date of birth reads "Age" while an age is typed. */
export const patientFieldLabel = (field, data = {}) => {
  if (field === 'dateOfBirth') return data.dobEstimated ? 'Age' : 'Date of Birth';
  return LABELS[field] || field;
};

/**
 * The rule for one field: null when it is fine, otherwise `{ kind, message }`. `kind` is
 * 'missing' for a required field left empty and 'invalid' for a value that is there but wrong;
 * `message` is the text shown under the field.
 *
 * `data` is the whole form, because most rules depend on another field: the date of birth decides
 * adult or child, the parent picked decides which CNIC is asked for. Two flags on an edited file
 * relax rules that came in after it was written: `_motherNameOptional` (no mother's name on file)
 * and `_parentCnicOptional` (no parent's CNIC on file). They let a missing value through, never a
 * malformed one, and the second only while the child has another number on file (see
 * parentCnicExcused).
 *
 * @param {string} field
 * @param {object} data  the form's values
 * @param {{ originalStatus?: string|null, onFile?: object|null }} [options]  on an edit, the status
 *        the file opened with and the file as it was loaded; both null for a new registration
 */
export const patientFieldProblem = (field, data, options = {}) => {
  const { onFile = null } = options;
  const value = data[field];
  switch (field) {
    case 'firstName':
      if (blank(value)) return missing('First name is required');
      if (text(value).length > NAME_MAX_LENGTH) return invalid(`First name can be at most ${NAME_MAX_LENGTH} characters`);
      if (!isValidName(value)) return invalid('First name must contain only letters and be at least 2 characters');
      return null;

    case 'lastName':
      if (blank(value)) return missing('Last name is required');
      if (text(value).length > NAME_MAX_LENGTH) return invalid(`Last name can be at most ${NAME_MAX_LENGTH} characters`);
      if (!isValidName(value)) return invalid('Last name must contain only letters and be at least 2 characters');
      return null;

    case 'phoneNumber':
      // Stored as "+92-3001234567"; a number with no digits is stored as '', whatever country was
      // picked, so blank is "missing" and a short or long number is "invalid".
      if (blank(value)) return missing('Phone number is required');
      if (!validatePhone(text(value))) return invalid(phoneDigitsMessage(value));
      return null;

    case 'dateOfBirth': {
      if (data.dobEstimated) {
        // Typed as an age. The date is worked out from it, so the messages talk about the age.
        if (!value) {
          // "0" years on its own is a baby whose months are not in yet, not an empty box.
          const zeroYears = !blank(data.ageYears) && Number(data.ageYears) === 0 && blank(data.ageMonths);
          return missing(zeroYears ? 'For a baby under 1 year, enter the months' : 'Age is required');
        }
        if (calculateAgeYears(value) > 120) return invalid('Enter an age of 120 years or less');
        return null;
      }
      if (!value) return missing('Date of birth is required');
      if (!isPlausibleBirthYear(value)) return invalid('Please enter a valid date of birth');
      const years = calculateAgeYears(value);
      if (years < 0) return invalid('Date of birth cannot be in the future');
      if (years > 120) return invalid('Please enter a valid date of birth');
      return null;
    }

    case 'gender':
      return value ? null : missing('Gender is required');

    // The child's own number, shown for a child only. Never required, but a number that is typed
    // has to be whole. Hidden, it is neither checked nor sent (see identityPayload).
    case 'bFormNumber':
      if (!isChildForm(data)) return null;
      if (!blank(value) && !isValidCnic(value)) return invalid('B-Form must be in format: 42401-1234567-8');
      return null;

    // An adult's own CNIC. Asked for whenever the field is on screen, which is until a date of birth
    // says child: the form shows it with its asterisk from the start, so the list names it from the
    // start too, and entering an adult's date never adds a field the receptionist was not told about.
    // (A missing date already holds the save, so this changes nothing about what saves.) A child has
    // no CNIC of their own.
    case 'cnic':
      if (isChildForm(data)) return null;
      if (blank(value)) return missing('CNIC is required for adults (18+)');
      if (!isValidCnic(value)) return invalid('CNIC must be in format: 42401-1234567-8');
      return null;

    // A child is identified by both parents' names and one parent's CNIC, whoever brings them in.
    case 'fatherName':
      return isChildForm(data) && blank(value) ? missing("Father's name is required for a child") : null;

    case 'motherName':
      return isChildForm(data) && !data._motherNameOptional && blank(value)
        ? missing("Mother's name is required for a child")
        : null;

    case 'parentCnicOwner':
      return isChildForm(data) && !parentCnicExcused(data, onFile) && !value
        ? missing("Pick which parent's CNIC you have")
        : null;

    // Only the CNIC of the parent picked above is asked for, and only then is it shown.
    case 'fatherCnic':
    case 'motherCnic': {
      const owner = field === 'fatherCnic' ? 'FATHER' : 'MOTHER';
      const whose = owner === 'FATHER' ? "Father's" : "Mother's";
      if (!isChildForm(data) || data.parentCnicOwner !== owner) return null;
      if (blank(value)) return parentCnicExcused(data, onFile) ? null : missing(`${whose} CNIC is required`);
      if (!isValidCnic(value)) return invalid(`${whose} CNIC must be in format: 42401-1234567-8`);
      return null;
    }

    case 'city':
      return blank(value) ? missing('City is required') : null;

    // An edit that changes the patient's status, in either direction, records when and why; so
    // does one that keeps a status the file holds without them (see statusNeedsRecording).
    case 'statusChangedAt':
      return statusNeedsRecording(data, options) && !value ? missing('Date is required for a status change') : null;

    case 'statusReason':
      return statusNeedsRecording(data, options) && blank(value)
        ? missing('Reason is required for a status change')
        : null;

    default:
      return null;
  }
};

/**
 * Every field stopping the save right now, touched or not, in on-screen order:
 * `[{ field, label, kind: 'missing'|'invalid', message }]`. Empty means the form can be saved.
 */
export const patientFormBlockers = (data, options = {}) =>
  PATIENT_FORM_FIELDS.reduce((list, field) => {
    const problem = patientFieldProblem(field, data, options);
    if (problem) list.push({ field, label: patientFieldLabel(field, data), ...problem });
    return list;
  }, []);

// ---- what the save sends -------------------------------------------------------------------------

/**
 * The identity numbers to send with a save: the adult's CNIC, the child's B-Form and the parent's
 * CNIC.
 *
 * Each of them is on screen for an adult or for a child only. A value in a field that is hidden
 * stays on the form, so a date typed through a wrong age on the way to the right one (typing 45
 * passes through 4) does not wipe a CNIC, a marital status or a B-Form. But a hidden field cannot
 * be seen or corrected, so what goes to the server for it is what the file already holds, never
 * what was typed there: a half-typed number would be refused outright, and a complete one typed for
 * the wrong kind of patient does not belong on the record.
 *
 * Only the parent picked in "Which parent's CNIC" has a CNIC sent, so switching the pick after
 * typing does not leave a number behind on the other parent.
 *
 * @param {object} data  the form's values
 * @param {{ onFile?: object|null }} [options]  the file as it was loaded, on an edit
 */
export const identityPayload = (data, { onFile = null } = {}) => {
  const child = isChildForm(data);
  const typed = (field) => text(data[field]).trim() || null;
  const kept = (field) => (onFile ? text(onFile[field]).trim() || null : null);
  const parentCnic = (field, owner) => {
    if (data.parentCnicOwner !== owner) return null;
    return child ? typed(field) : kept(field);
  };
  return {
    cnic: child ? kept('cnic') : typed('cnic'),
    bFormNumber: child ? typed('bFormNumber') : kept('bFormNumber'),
    fatherCnic: parentCnic('fatherCnic', 'FATHER'),
    motherCnic: parentCnic('motherCnic', 'MOTHER'),
  };
};

/**
 * The status fields to send with a save. A new registration is Active with nothing recorded. An
 * edit that records a status (see statusNeedsRecording) sends the form's date and reason. An edit
 * that leaves a status other than Active alone sends the date and reason on file: the server wants
 * them with every save of such a status, and sending no status at all is not a way round that,
 * because the server reads a missing status as Active and would quietly reactivate the patient.
 *
 * @param {object} data  the form's values
 * @param {{ originalStatus?: string|null, onFile?: object|null }} [options]
 */
export const statusPayload = (data, options = {}) => {
  const { originalStatus = null, onFile = null } = options;
  const status = data.status || 'ACTIVE';
  if (statusNeedsRecording(data, options)) {
    return { status, statusChangedAt: data.statusChangedAt || null, statusReason: text(data.statusReason).trim() || null };
  }
  if (originalStatus != null && status !== 'ACTIVE' && onFile) {
    return {
      status,
      statusChangedAt: onFile.statusChangedAt || null,
      statusReason: text(onFile.statusReason).trim() || null,
    };
  }
  return { status, statusChangedAt: null, statusReason: null };
};

// ---- saying it in the footer ---------------------------------------------------------------------

/** "A", "A and B", "A, B and C". */
const joinAnd = (items) =>
  items.length <= 1 ? (items[0] || '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/** Up to `room` labels by name, the rest as "and N more". */
const nameSome = (labels, room) => {
  if (labels.length <= room) return joinAnd(labels);
  return `${labels.slice(0, room).join(', ')} and ${labels.length - room} more`;
};

/**
 * The list as the footer tells it while the receptionist is still typing. A value in the field that
 * has focus is work in progress, not yet a mistake, so it counts as still needed (amber) until they
 * leave the field; otherwise the first letter of a name, or each digit of a phone before the tenth,
 * would turn the line red on nearly every keystroke. Only the footer's wording changes: the list,
 * the button and the save are the same.
 */
export const whileTyping = (blockers, focusedField) =>
  focusedField
    ? blockers.map((b) => (b.field === focusedField && b.kind === 'invalid' ? { ...b, kind: 'missing' } : b))
    : blockers;

/**
 * The footer line for a list of blockers, or null when there are none.
 *
 *   only missing fields  amber  "Still needed: Last Name, Phone Number, Date of Birth and 2 more."
 *   any invalid value    red    "Check: Phone Number. Still needed: Gender, City and 1 more."
 *
 * A value typed wrong is named first and turns the line red: it is a mistake to fix, where an empty
 * field is only work not done yet. At most three fields are named in all (a wrong value always
 * keeps at least one name for the missing part), so the line stays short beside its button.
 */
export const describeBlockers = (blockers, maxNamed = 3) => {
  if (!blockers || blockers.length === 0) return null;
  const invalidLabels = blockers.filter((b) => b.kind === 'invalid').map((b) => b.label);
  const missingLabels = blockers.filter((b) => b.kind !== 'invalid').map((b) => b.label);
  if (invalidLabels.length === 0) {
    return { tone: 'amber', text: `Still needed: ${nameSome(missingLabels, maxNamed)}.` };
  }
  const parts = [`Check: ${nameSome(invalidLabels, maxNamed)}.`];
  if (missingLabels.length > 0) {
    const room = Math.max(1, maxNamed - Math.min(invalidLabels.length, maxNamed));
    parts.push(`Still needed: ${nameSome(missingLabels, room)}.`);
  }
  return { tone: 'red', text: parts.join(' ') };
};

// ---- taking the receptionist there ---------------------------------------------------------------

const TYPED_CONTROL = [
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([readonly]):not(:disabled)',
  'textarea:not([readonly]):not(:disabled)',
].join(', ');
const ANY_CONTROL = [
  'input:not([type="hidden"]):not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  'button:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Scrolls the field marked `data-field="<field>"` inside `container` into view and focuses its
 * control: the box a value is typed into when it has one (the number beside the country code, say),
 * otherwise its picker button. Says whether it found the field.
 */
export const focusFieldByName = (container, field) => {
  const wrapper = container && field ? container.querySelector(`[data-field="${field}"]`) : null;
  if (!wrapper) return false;
  if (typeof wrapper.scrollIntoView === 'function') {
    wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  const control = wrapper.querySelector(TYPED_CONTROL) || wrapper.querySelector(ANY_CONTROL);
  if (control) control.focus({ preventScroll: true });
  return true;
};
